/**
 * ARCHX AI — Live in-portal 3D layering viewer (v1).
 *
 * Opened per case (/cases/:id/viewer). Overlays the case's real Storage files
 * as toggleable layers in one Three.js scene: preop scans, the engine
 * provisional STL, and implant position/axis rods (from the photogrammetry CSV).
 *
 * v1 scope: load + overlay + toggle + colour/opacity + orbit. No editing or
 * measurement yet. Layers render in their native file coordinates — cross-layer
 * registration is a later step (needs engine-exported transforms).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase';
import SceneManager from '../three/SceneManager';
import { loadMeshFromURL, parseImplantCSV, buildImplantRods, buildDeviationMarkers, deviationColor, extOf } from '../three/loaders';
import './CaseViewer.css';

const PREOP_COLORS = ['#9db4c0', '#7fa6b8', '#b0c4b1', '#c9b48a'];
const PROVISIONAL_COLOR = '#c8922a';
const IMPLANT_COLOR = '#e2483d';

/** Build the ordered layer descriptors for a case. */
function buildLayerPlan(caseData) {
  const files = caseData.files || [];
  const practiceId = caseData.practiceId;
  const caseNumber = caseData.caseNumber;
  const layers = [];

  // 1. Preop scans (preop3d IOS files) — one layer each, .stl or .ply.
  files
    .filter((f) => f.category === 'preop3d')
    .forEach((f, i) => {
      const ext = extOf(f.name || f.path);
      if (ext !== 'stl' && ext !== 'ply') return;
      layers.push({
        key: `preop:${f.path}`,
        label: (f.name || 'preop scan').replace(/\.(stl|ply)$/i, ''),
        kind: 'mesh',
        path: f.path,
        ext,
        color: PREOP_COLORS[i % PREOP_COLORS.length],
      });
    });

  // 2. Engine provisional output (skipped at load time if not generated yet).
  layers.push({
    key: 'provisional',
    label: 'Provisional (engine)',
    kind: 'mesh',
    storagePath: `cases/${practiceId}/${caseNumber}/output/provisional.stl`,
    ext: 'stl',
    color: PROVISIONAL_COLOR,
  });

  // 2b. Per-implant registration deviation (engine output/registration.json).
  // Colored markers = how far each implant was placed from the planned channel.
  layers.push({
    key: 'deviation',
    label: 'Implant deviation',
    kind: 'deviation',
    storagePath: `cases/${practiceId}/${caseNumber}/output/registration.json`,
    dataLayer: true,
  });

  // 3. Implants — photogrammetry CSV → rods; or a photogrammetry mesh file.
  const photo = files.find((f) => f.category === 'photogrammetry');
  if (photo) {
    const ext = extOf(photo.name || photo.path);
    if (ext === 'csv') {
      layers.push({ key: 'implants', label: 'Implants (positions + axes)', kind: 'rods', path: photo.path, color: IMPLANT_COLOR });
    } else if (ext === 'stl' || ext === 'ply') {
      layers.push({ key: `photo:${photo.path}`, label: photo.name || 'photogrammetry', kind: 'mesh', path: photo.path, ext, color: IMPLANT_COLOR });
    }
  }

  return layers;
}

export default function CaseViewer({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const sceneRef = useRef(null);

  const [caseData, setCaseData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // UI state per layer: {key,label,color,opacity,visible,status,error,t:[3],r:[3],showAlign}
  const [layers, setLayers] = useState([]);
  const [dirty, setDirty] = useState(false);   // unsaved manual-alignment changes
  const [saving, setSaving] = useState(false);

  // Load the case doc and verify ownership.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'cases', id));
        if (cancelled) return;
        if (!snap.exists()) { setLoadError('Case not found.'); return; }
        const data = { id: snap.id, ...snap.data() };
        if (data.practiceId && user?.uid && data.practiceId !== user.uid) {
          setLoadError('You do not have access to this case.'); return;
        }
        setCaseData(data);
      } catch (e) {
        if (!cancelled) setLoadError(e.message || 'Failed to load case.');
      }
    })();
    return () => { cancelled = true; };
  }, [id, user]);

  const plan = useMemo(() => (caseData ? buildLayerPlan(caseData) : []), [caseData]);

  // Init the scene and load layers once the case + container are ready.
  useEffect(() => {
    if (!caseData || !containerRef.current) return undefined;
    let cancelled = false;

    const sm = new SceneManager(containerRef.current);
    sceneRef.current = sm;

    // Saved manual alignments persisted on the case doc (the bridge toward
    // automated registration: these become labeled transforms to seed/validate).
    const saved = {};
    (caseData.viewerAlignments || []).forEach((a) => { if (a && a.key) saved[a.key] = a; });

    setLayers(plan.map((l) => ({
      key: l.key, label: l.label, color: l.color, opacity: 1, visible: true,
      status: 'pending', error: null, showAlign: false, dataLayer: !!l.dataLayer, dev: null,
      t: saved[l.key]?.t || [0, 0, 0], r: saved[l.key]?.r || [0, 0, 0],
    })));

    const update = (key, patch) => setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));

    (async () => {
      let anyLoaded = false;
      for (const l of plan) {
        if (cancelled) return;
        update(l.key, { status: 'loading' });
        try {
          // resolve the storage download URL
          let url;
          try {
            url = await getDownloadURL(ref(storage, l.path || l.storagePath));
          } catch (e) {
            // provisional / optional layers may not exist yet
            update(l.key, { status: 'empty' });
            continue;
          }
          if (cancelled) return;

          let object3d;
          let devData = null;
          if (l.kind === 'rods') {
            const text = await (await fetch(url)).text();
            const implants = parseImplantCSV(text);
            if (!implants.length) { update(l.key, { status: 'empty' }); continue; }
            object3d = buildImplantRods(implants);
          } else if (l.kind === 'deviation') {
            const report = await (await fetch(url)).json();
            if (!report.implants?.length) { update(l.key, { status: 'empty' }); continue; }
            object3d = buildDeviationMarkers(report);
            devData = report.implants.map((im) => ({ id: im.id, mm: im.deviation_mm }));
          } else {
            object3d = await loadMeshFromURL(url, l.ext);
          }
          if (cancelled) return;

          sm.addLayer(l.key, object3d, l.color, 1, { keepMaterials: !!l.dataLayer });
          if (saved[l.key]) sm.setLayerTransform(l.key, saved[l.key].t, saved[l.key].r);
          update(l.key, { status: 'loaded', dev: devData });
          if (!anyLoaded) { sm.fitToScene(); anyLoaded = true; }
        } catch (e) {
          if (!cancelled) update(l.key, { status: 'error', error: e.message || 'load failed' });
        }
      }
      if (!cancelled) sm.fitToScene();
    })();

    return () => {
      cancelled = true;
      sm.dispose();
      sceneRef.current = null;
    };
  }, [caseData, plan]);

  const onToggle = (key, visible) => {
    sceneRef.current?.setVisible(key, visible);
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, visible } : l)));
  };
  const onColor = (key, color) => {
    sceneRef.current?.setColor(key, color);
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, color } : l)));
  };
  const onOpacity = (key, opacity) => {
    sceneRef.current?.setOpacity(key, opacity);
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, opacity } : l)));
  };

  // --- manual alignment (toward automation: transforms are persisted as data) ---
  const applyTR = (key, t, r) => {
    sceneRef.current?.setLayerTransform(key, t, r);
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, t, r } : l)));
    setDirty(true);
  };
  const onTranslate = (l, axis, val) => {
    const t = [...l.t]; t[axis] = Number.isFinite(val) ? val : 0; applyTR(l.key, t, l.r);
  };
  const onRotate = (l, axis, val) => {
    const r = [...l.r]; r[axis] = Number.isFinite(val) ? val : 0; applyTR(l.key, l.t, r);
  };
  const onResetAlign = (l) => applyTR(l.key, [0, 0, 0], [0, 0, 0]);
  const onToggleAlign = (key) =>
    setLayers((prev) => prev.map((l) => (l.key === key ? { ...l, showAlign: !l.showAlign } : l)));

  // Coarse auto rung: snap this layer's centroid onto the first loaded preop scan.
  const onSnapToPreop = (l) => {
    const sm = sceneRef.current; if (!sm) return;
    const preop = layers.find((x) => x.key.startsWith('preop:') && x.status === 'loaded');
    if (!preop) return;
    const target = sm.getCentroid(preop.key);
    const cur = sm.getCentroid(l.key);
    if (!target || !cur) return;
    const t = [l.t[0] + (target.x - cur.x), l.t[1] + (target.y - cur.y), l.t[2] + (target.z - cur.z)];
    applyTR(l.key, t, l.r);
  };

  const onSaveAlign = async () => {
    setSaving(true);
    try {
      const payload = layers
        .filter((l) => l.status === 'loaded')
        .map((l) => ({ key: l.key, t: l.t, r: l.r }));
      await updateDoc(doc(db, 'cases', id), { viewerAlignments: payload });
      setDirty(false);
    } catch (e) {
      alert(`Could not save alignment: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const hasPreop = layers.some((x) => x.key.startsWith('preop:') && x.status === 'loaded');

  const title = caseData
    ? `${caseData.caseNumber || 'Case'}${caseData.patientLast ? ` — ${caseData.patientLast}, ${caseData.patientFirst || ''}` : ''}`
    : 'Loading…';

  return (
    <div className="viewer-root">
      <div className="viewer-topbar">
        <button className="viewer-back" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        <span className="viewer-title">{title}</span>
        <button className="viewer-recenter" onClick={() => sceneRef.current?.fitToScene()} disabled={!caseData}>
          ⤢ Recenter
        </button>
      </div>

      {loadError ? (
        <div className="viewer-error">{loadError}</div>
      ) : (
        <div className="viewer-stage">
          <div className="viewer-canvas" ref={containerRef} />

          <div className="viewer-panel">
            <div className="viewer-panel-head">
              <span>Layers</span>
              <button
                className="align-save"
                onClick={onSaveAlign}
                disabled={!dirty || saving}
                title="Save manual alignments to this case"
              >
                {saving ? 'Saving…' : dirty ? 'Save alignment' : 'Saved'}
              </button>
            </div>
            {layers.length === 0 && <div className="viewer-empty">No renderable files for this case.</div>}
            {layers.map((l) => (
              <div className="layer-row" key={l.key}>
                <div className="layer-row-top">
                  <label className="layer-toggle">
                    <input
                      type="checkbox"
                      checked={l.visible}
                      disabled={l.status !== 'loaded'}
                      onChange={(e) => onToggle(l.key, e.target.checked)}
                    />
                    <span className="layer-label" title={l.label}>{l.label}</span>
                  </label>
                  {!l.dataLayer && (
                    <input
                      type="color"
                      className="layer-color"
                      value={l.color}
                      disabled={l.status !== 'loaded'}
                      onChange={(e) => onColor(l.key, e.target.value)}
                    />
                  )}
                </div>
                <div className="layer-row-bot">
                  <input
                    type="range"
                    min="0" max="1" step="0.05"
                    value={l.opacity}
                    disabled={l.status !== 'loaded'}
                    onChange={(e) => onOpacity(l.key, parseFloat(e.target.value))}
                  />
                  <span className={`layer-status status-${l.status}`}>
                    {l.status === 'loading' && <span className="spinner" />}
                    {l.status === 'loading' ? 'loading…'
                      : l.status === 'empty' ? 'not available'
                      : l.status === 'error' ? (l.error || 'error')
                      : l.status === 'pending' ? 'queued'
                      : ''}
                  </span>
                  {l.status === 'loaded' && !l.dataLayer && (
                    <button
                      className={`align-toggle ${l.showAlign ? 'on' : ''}`}
                      onClick={() => onToggleAlign(l.key)}
                      title="Manual alignment"
                    >⚙</button>
                  )}
                </div>

                {l.dataLayer && l.status === 'loaded' && l.dev && (
                  <div className="dev-panel">
                    {l.dev.map((d) => (
                      <div className="dev-row" key={d.id}>
                        <span className="dev-dot" style={{ background: `#${deviationColor(d.mm).getHexString()}` }} />
                        <span className="dev-id">{d.id}</span>
                        <span className="dev-mm">{d.mm == null ? '—' : `${d.mm.toFixed(2)} mm`}</span>
                      </div>
                    ))}
                    <div className="dev-legend">
                      <span style={{ color: `#${deviationColor(0).getHexString()}` }}>0</span>
                      <span className="dev-legend-bar" />
                      <span style={{ color: `#${deviationColor(2).getHexString()}` }}>≥2mm</span>
                      <span className="dev-legend-note">placement vs plan</span>
                    </div>
                  </div>
                )}

                {l.showAlign && l.status === 'loaded' && (
                  <div className="align-panel">
                    {['X', 'Y', 'Z'].map((ax, i) => (
                      <div className="align-row" key={`t${i}`}>
                        <span className="align-lbl">move {ax}</span>
                        <input type="number" step="0.5" value={l.t[i]}
                          onChange={(e) => onTranslate(l, i, parseFloat(e.target.value))} />
                        <span className="align-unit">mm</span>
                      </div>
                    ))}
                    {['X', 'Y', 'Z'].map((ax, i) => (
                      <div className="align-row" key={`r${i}`}>
                        <span className="align-lbl">rot {ax}</span>
                        <input type="number" step="1" value={l.r[i]}
                          onChange={(e) => onRotate(l, i, parseFloat(e.target.value))} />
                        <span className="align-unit">°</span>
                      </div>
                    ))}
                    <div className="align-actions">
                      {hasPreop && !l.key.startsWith('preop:') && (
                        <button onClick={() => onSnapToPreop(l)} title="Snap centroid onto the preop scan">Snap → preop</button>
                      )}
                      <button onClick={() => onResetAlign(l)}>Reset</button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
